import { useEffect } from 'react';
import { useLocation, useSearchParams, Link } from 'react-router-dom';
import { useReveal } from '@/hooks/useReveal';
import { useHeroParallax } from '@/hooks/useHeroParallax';
import { scrollToSection } from '@/hooks/useLenis';
import PageLoader from '@/components/PageLoader';
import HeroCrumbs from '@/components/HeroCrumbs';
import { GUIDE_ICON } from '@/components/GuideIcons';
import LcdDisplay from '@/components/LcdDisplay';
import SaasDashboard from '@/components/SaasDashboard';
import BizLoopDiagram from '@/components/BizLoopDiagram';
import { GUIDES, type ProductDomain, type GuideBlock, type GuideSection } from '@/data/mockData';
import s from './GuidePage.module.scss';

const GUIDE_HERO = '/images/hero/hero-guide.jpg'; // 설치안내 공통 히어로(2026-09-08 사용자 지정)

// ── 설치안내 단일 앵커 페이지 (/guide — 2026-09-08 사용자 재설계) ─────────────────────────────
//   evstech 4원본(제조업·방지시설·배출시설·설치대상)을 앵커 섹션으로. 서브메뉴 → /guide#id.
//   디자인 = evs.com/applications 식(라이트·중앙 헤딩+시안 강조·플랫 SVG 아이콘·페이드업 리빌). 배경 white.
//   수직으로 풍부하게(쇼핑몰식) — 요약 지양. SVG=GUIDE_ICON.

// lazy 이미지 로드 완료 시 페이드인 트리거(data-ready 세팅). [2026-09-09 사용자] 흰색 팝인 방지.
//   ref = 마운트 시점에 이미 로드(캐시)된 경우 즉시 처리(onLoad 미발화 대비) / onLoad = 늦게 도착하는 경우.
function markImgReady(el: HTMLImageElement | null) {
  if (el && el.complete && el.naturalWidth > 0) el.dataset.ready = '1';
}
function onImgLoad(e: React.SyntheticEvent<HTMLImageElement>) {
  e.currentTarget.dataset.ready = '1';
}

// 제목 내 시안 강조(accent 부분만 색). 없으면 그대로.
function Title({ text, accent }: { text: string; accent?: string }) {
  if (!accent || !text.includes(accent)) return <>{text}</>;
  const [a, b] = text.split(accent);
  return <>{a}<em className={s.accent}>{accent}</em>{b}</>;
}

// 블록 렌더 — kind별. 각 블록이 스스로 리빌(fade+up).
function Block({ b }: { b: GuideBlock }) {
  const ref = useReveal<HTMLDivElement>();
  return (
    <div className={`${s.block} anim-scroll`} ref={ref}>
      {b.title && b.kind !== 'lcd' && b.kind !== 'diagram' && <h3 className={s.blockTitle}>{b.title}</h3>}

      {b.kind === 'lead' && <p className={s.lead}>{b.body}</p>}

      {/* 사업영역 순환 다이어그램(WHAT WE DO) — 다크 풀블리드. 제조업 섹션 데이터그래픽 교체(2026-09-09). */}
      {b.kind === 'diagram' && <BizLoopDiagram variant={b.diagram || 'env'} />}

      {/* 통합 관제 SaaS 미니 대시보드(요약 KPI + WBGT 추이 그래프) */}
      {b.kind === 'saas' && <SaasDashboard />}

      {/* 컴팩트 기능 목록(아이콘+텍스트 2열) — 텍스트 위주·풍성·심플 */}
      {b.kind === 'featlist' && (
        <ul className={s.featList}>
          {b.features!.map((f, i) => (
            <li key={i} className={s.featItem}>
              <span className={s.featItemIcon}>{GUIDE_ICON[f.icon]}</span>
              <span className={s.featItemText}>
                <span className={s.featItemTitle}>{f.title}</span>
                <span className={s.featItemDesc}>{f.desc}</span>
              </span>
            </li>
          ))}
        </ul>
      )}

      {/* LCD = 좌 설명 + 우 기기(2단). 한 줄 통째 차지 방지(사용자 2026-09-08). */}
      {b.kind === 'lcd' && (
        <div className={s.lcdRow}>
          <div className={s.lcdText}>
            <span className={s.lcdEyebrow}>ON-SITE DISPLAY</span>
            {b.title && <h3 className={s.lcdTitle}>{b.title}</h3>}
            {b.body && <p className={s.lcdBody}>{b.body}</p>}
          </div>
          <div className={s.lcdWrap}><LcdDisplay /></div>
        </div>
      )}

      {b.kind === 'features' && (
        <ul className={s.features}>
          {b.features!.map((f, i) => (
            <li key={i} className={s.feature}>
              <span className={s.featIcon}>{GUIDE_ICON[f.icon]}</span>
              <span className={s.featTitle}>{f.title}</span>
              <span className={s.featDesc}>{f.desc}</span>
            </li>
          ))}
        </ul>
      )}


      {b.kind === 'table' && (
        <div className={s.tableWrap}>
          <table className={s.table}>
            <thead><tr>{b.table!.head.map((h, i) => <th key={i}>{h}</th>)}</tr></thead>
            <tbody>
              {b.table!.rows.map((r, i) => (
                <tr key={i}>{r.map((cell, j) => (j === 0 ? <th key={j} scope="row">{cell}</th> : <td key={j}>{cell}</td>))}</tr>
              ))}
            </tbody>
          </table>
          {b.table!.note && <p className={s.tableNote}>{b.table!.note}</p>}
        </div>
      )}

      {b.kind === 'facts' && (
        <dl className={s.facts}>
          {b.facts!.map((f, i) => (
            <div key={i} className={s.fact}>
              {f.icon && <span className={s.factIcon}>{GUIDE_ICON[f.icon]}</span>}
              <dt>{f.k}</dt><dd>{f.v}</dd>
            </div>
          ))}
        </dl>
      )}

      {b.kind === 'chips' && (
        <ul className={s.chips}>
          {b.chips!.map((c, i) => <li key={i}>{c.t}</li>)}
        </ul>
      )}

      {/* 체감온도 4단계(안전 전용 계단형) — 단계별 색 상승(초록→노랑→주황→빨강) */}
      {b.kind === 'stages' && (
        <ol className={s.stages}>
          {b.stages!.map((st, i) => (
            <li key={i} className={s.stage} style={{ ['--sc' as string]: st.color } as React.CSSProperties}>
              <span className={s.stageBar} />
              <span className={s.stageTop}><span className={s.stageLevel}>{st.level}</span><span className={s.stageTemp}>{st.temp}</span></span>
              <span className={s.stageAction}>{st.action}</span>
            </li>
          ))}
        </ol>
      )}

      {b.kind === 'product' && (
        <div className={s.product}>
          <div className={s.productInner}>
            <div className={`${s.prodMedia} ${s.reveal}`}><img src={b.product!.image} alt={b.product!.name} loading="lazy" ref={markImgReady} onLoad={onImgLoad} /></div>
            <div className={s.prodInfo}>
              {b.product!.badges && (
                <div className={s.prodBadges}>
                  {b.product!.badges.map((bd, i) => <span key={i} className={i % 2 === 0 ? s.badgeBlue : s.badgeOrange}>{bd}</span>)}
                </div>
              )}
              <span className={s.prodName}>{b.product!.name}</span>
              {b.product!.paras.map((p, i) => <p key={i} className={s.prodPara}>{p}</p>)}
              <dl className={s.prodSpecs}>
                {b.product!.specs.map((sp, i) => <div key={i}><dt>{sp.k}</dt><dd>{sp.v}</dd></div>)}
              </dl>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// 좌우 교대 이미지+텍스트 행 — 행마다 독립 리빌(스크롤 진입 시 이미지 클립 와이프) + 고유 강조색(--accent).
function RevealRow({ r, alt }: { r: NonNullable<GuideBlock['rows']>[number]; alt: boolean }) {
  const ref = useReveal<HTMLDivElement>();
  return (
    <div className={`${s.row} ${alt ? s.rowAlt : ''} anim-scroll`} ref={ref} style={{ ['--accent' as string]: r.accent || '#00AEEF' } as React.CSSProperties}>
      <div className={`${s.rowMedia} ${s.reveal}`}><img src={r.image} alt={r.title} loading="lazy" ref={markImgReady} onLoad={onImgLoad} /></div>
      <div className={s.rowText}>
        <div className={s.rowKicker}>
          {r.icon && <span className={s.rowIcon}>{GUIDE_ICON[r.icon]}</span>}
          {r.subtitle && <span className={s.rowSub}>{r.subtitle}</span>}
        </div>
        <h3 className={s.rowTitle}>{r.title}</h3>
        <p className={s.rowDesc}>{r.desc}</p>
        {r.meta && (
          <dl className={s.rowMeta}>
            {r.meta.map((m, j) => <div key={j}><dt>{m.k}</dt><dd>{m.v}</dd></div>)}
          </dl>
        )}
      </div>
    </div>
  );
}

function RowsBlock({ b }: { b: GuideBlock }) {
  return (
    <div className={s.rows}>
      {b.title && <h3 className={s.blockTitle}>{b.title}</h3>}
      {b.rows!.map((r, i) => <RevealRow key={i} r={r} alt={i % 2 === 1} />)}
    </div>
  );
}

function Section({ sec }: { sec: GuideSection }) {
  const head = useReveal<HTMLDivElement>();
  return (
    <section id={sec.id} className={`${s.section} ${sec.tone === 'soft' ? s.soft : s.light}`}>
      <div className={s.inner}>
        <div className={`${s.secHead} anim-scroll`} ref={head}>
          <p className={s.eyebrow}>{sec.eyebrow}</p>
          <h2 className={s.secTitle}><Title text={sec.title} accent={sec.accent} /></h2>
          {sec.intro && <p className={s.secIntro}>{sec.intro}</p>}
        </div>
        {sec.blocks.map((b, i) => (b.kind === 'rows' ? <RowsBlock key={i} b={b} /> : <Block key={i} b={b} />))}
      </div>
    </section>
  );
}

export default function GuidePage() {
  const heroBgRef = useHeroParallax<HTMLDivElement>();
  const { hash } = useLocation();
  const [params] = useSearchParams();
  // [2026-09-08 사용자] 설치안내를 안전/환경 탭으로 분할. ?tab=safety|environment(기본=환경). 도메인 색 테마(--gc).
  const tab: ProductDomain = params.get('tab') === 'safety' ? 'safety' : 'environment';
  const guide = GUIDES[tab];

  // 해시 앵커 스크롤(서브메뉴 딥링크) — 긴 페이지라 마운트 직후엔 lenis가 이른 문서높이로 목표를 클램프해
  //   섹션에 못 미치는 문제가 있었음. → 초기 1회 + 레이아웃/이미지/리빌 안정 후 보정 재발화(총 3회). 탭 변경 시에도 재정렬.
  useEffect(() => {
    if (!hash) { window.scrollTo(0, 0); return; }
    const id = hash.replace('#', '');
    let cancelled = false;
    const fire = () => { if (!cancelled) scrollToSection(id); };
    const timers = [60, 450, 950].map((d) => window.setTimeout(fire, d));
    return () => { cancelled = true; timers.forEach(clearTimeout); };
  }, [hash, tab]);

  const { hero } = guide;
  return (
    <main className={`${s.page} ${tab === 'safety' ? s.tSafety : s.tEnv}`}>
      <PageLoader />
      {/* 히어로 — 설치안내 공통 이미지 + 도메인 슬로건(2단). 색 구분자(rule)=도메인 색. */}
      <header className={s.hero}>
        <div ref={heroBgRef} className={s.heroBg} style={{ backgroundImage: `url(${GUIDE_HERO})` }} />
        <div className={s.heroOverlay} />
        <HeroCrumbs items={['HOME', '환경', '배출·방지시설 안내']} /> {/* [2026-10-11] /guide는 B안에서 환경 하위(배출·방지시설 안내)로 이동 — 브레드크럼 부모 교정(구 '설치안내' 오류) */}
        <div className={s.heroInner}>
          <h1 className={s.heroTitle}>{hero.title}</h1>
          <span className={s.heroRule} />
          <p className={s.heroSub}>{hero.sub}</p>
        </div>
      </header>

      {/* 안전/환경 탭(뉴스와 동일 패턴) — 활성 탭은 도메인 색 */}
      <nav className={s.tabs} aria-label="설치안내 도메인">
        <Link to="/guide?tab=safety" className={`${s.tab} ${tab === 'safety' ? s.tabOn : ''}`}>안전 설치안내</Link>
        <Link to="/guide?tab=environment" className={`${s.tab} ${tab === 'environment' ? s.tabOn : ''}`}>환경 설치안내</Link>
      </nav>

      {guide.sections.map((sec) => <Section key={sec.id} sec={sec} />)}
    </main>
  );
}
